/**
 * Small building blocks shared by the charts: the card frame, chip
 * toggles, legend swatches, the tooltip box and the info tip.
 */
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RANGE_DAYS, type RangeDays } from '../../lib/chartData.ts';

export function ChartCard({
  title,
  actions,
  children,
}: Readonly<{ title: string; actions?: ReactNode; children: ReactNode }>) {
  return (
    <section className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function Chip({
  pressed,
  onClick,
  children,
}: Readonly<{ pressed: boolean; onClick: () => void; children: ReactNode }>) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
        pressed
          ? 'bg-(--color-accent)/15 text-(--color-accent) border-(--color-accent)/40'
          : 'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border) hover:text-(--color-text-primary)'
      }`}
    >
      {children}
    </button>
  );
}

/** Single-choice chip row (range, unit, axis, tab). */
export function ChipGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: Readonly<{
  label: string;
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}>) {
  return (
    <fieldset className="flex flex-wrap gap-1.5">
      <legend className="sr-only">{label}</legend>
      {options.map((o) => (
        <Chip key={o.value} pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </Chip>
      ))}
    </fieldset>
  );
}

/** 6 weeks / 3 months / 1 year. */
export function RangeChips({
  value,
  onChange,
}: Readonly<{ value: RangeDays; onChange: (value: RangeDays) => void }>) {
  const { t } = useTranslation();
  return (
    <ChipGroup
      label={t('charts.range.label')}
      value={value}
      onChange={onChange}
      options={RANGE_DAYS.map((d) => ({ value: d, label: t(`charts.range.d${d}`) }))}
    />
  );
}

/** A short line in the series colour and dash, for chips and legends. */
export function SeriesSwatch({
  color,
  dash,
  bar = false,
}: Readonly<{ color: string; dash?: string; bar?: boolean }>) {
  return (
    <svg width="16" height="8" aria-hidden="true" className="shrink-0">
      {bar ? (
        <rect x="4" y="0" width="8" height="8" fill={color} />
      ) : (
        <line x1="0" y1="4" x2="16" y2="4" stroke={color} strokeWidth="2" strokeDasharray={dash} />
      )}
    </svg>
  );
}

export function LegendItem({
  color,
  dash,
  bar,
  label,
}: Readonly<{ color: string; dash?: string; bar?: boolean; label: string }>) {
  return (
    <li className="flex items-center gap-1.5">
      <SeriesSwatch color={color} dash={dash} bar={bar} />
      <span>{label}</span>
    </li>
  );
}

export function Legend({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-(--color-text-secondary)">
      {children}
    </ul>
  );
}

export function TooltipBox({ title, children }: Readonly<{ title: string; children: ReactNode }>) {
  return (
    <div className="rounded-lg bg-(--color-bg-primary)/95 border border-(--color-border) px-3 py-2 text-xs text-(--color-text-primary) shadow-lg">
      <div className="font-medium mb-1">{title}</div>
      <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">{children}</dl>
    </div>
  );
}

export function TooltipRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <>
      <dt className="text-(--color-text-secondary)">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </>
  );
}

/** "i" button that shows an explanation underneath. */
export function InfoTip({ label, text }: Readonly<{ label: string; text: string }>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        aria-label={t('charts.infoAbout', { name: label })}
        onClick={() => setOpen((o) => !o)}
        className="w-4 h-4 rounded-full border border-(--color-border) text-[10px] leading-none text-(--color-text-secondary) hover:text-(--color-text-primary)"
      >
        i
      </button>
      {open && (
        <p id={id} className="basis-full text-[11px] text-(--color-text-secondary) font-normal normal-case tracking-normal">
          {text}
        </p>
      )}
    </>
  );
}

export function ChartMessage({ children, role }: Readonly<{ children: ReactNode; role?: 'alert' }>) {
  return (
    <div
      role={role}
      className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-4 py-6 text-center text-sm text-(--color-text-secondary)"
    >
      {children}
    </div>
  );
}

export function ChartSkeleton() {
  return (
    <div
      data-testid="chart-skeleton"
      className="h-56 rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) animate-pulse"
    />
  );
}
